import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import { Reveal, stagger, fadeUp } from "../../lib/motion";
import { SERVICES } from "../../data/site";

export const Services = () => {
  const featured = SERVICES.filter((s) => s.featured);
  const rest = SERVICES.filter((s) => !s.featured);
  const go = () => document.querySelector("#contact")?.scrollIntoView({ behavior: "smooth" });

  return (
    <section id="services" data-testid="services-section" className="py-24 lg:py-32 relative">
      <div className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
          <div>
            <Reveal>
              <p className="overline mb-4">What we do</p>
            </Reveal>
            <Reveal delay={0.1}>
              <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl tracking-tight text-[var(--text)] max-w-2xl leading-[1.02]">
                A full financial life, <span className="text-gold italic">handled.</span>
              </h2>
            </Reveal>
          </div>
          <Reveal delay={0.2}>
            <p className="max-w-sm text-white/60 text-base leading-relaxed">
              Our core is investing — Mutual Funds, SIPs and Specialized Funds — wrapped in
              education, protection and goal planning for the whole family.
            </p>
          </Reveal>
        </div>

        {/* Featured trio — asymmetric large cards */}
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          className="grid grid-cols-1 md:grid-cols-12 gap-5 mb-5"
        >
          {featured.map((s, i) => {
            const Icon = s.icon;
            const span = i === 0 ? "md:col-span-6" : "md:col-span-3";
            return (
              <motion.div
                key={s.id}
                variants={fadeUp}
                data-testid={`service-${s.id}`}
                className={`${span} group relative overflow-hidden rounded-2xl bg-[#111827] border border-[#2A3441] p-8 lg:p-10 hover:border-[var(--accent)]/60 transition-colors duration-500`}
              >
                <div className="absolute -right-10 -top-10 w-40 h-40 rounded-full bg-[var(--accent)]/5 blur-2xl group-hover:bg-[var(--accent)]/15 transition-colors duration-700" />
                <div className="relative flex items-start justify-between mb-14">
                  <span className="w-12 h-12 rounded-lg bg-[var(--accent)]/12 flex items-center justify-center text-gold">
                    <Icon size={22} />
                  </span>
                  <span className="font-display text-white/15 text-4xl">{s.tag}</span>
                </div>
                <h3 className="relative font-display text-2xl lg:text-3xl text-[var(--text)] mb-4 leading-tight">
                  {s.title}
                </h3>
                <p className="relative text-white/60 text-base leading-relaxed max-w-md">{s.blurb}</p>
                <span className="relative mt-6 inline-block text-xs uppercase tracking-[0.2em] text-gold">
                  Core Service
                </span>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Supporting services grid */}
        <motion.div
          variants={stagger}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-60px" }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5"
        >
          {rest.map((s) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.id}
                variants={fadeUp}
                data-testid={`service-${s.id}`}
                className="group rounded-2xl bg-[#0d1424] border border-[#2A3441] p-7 hover:-translate-y-1 hover:border-[var(--accent)]/50 transition-all duration-400"
              >
                <span className="w-10 h-10 rounded-lg bg-white/5 flex items-center justify-center text-gold mb-6">
                  <Icon size={19} />
                </span>
                <h3 className="font-display text-xl text-[var(--text)] mb-2 leading-snug">{s.title}</h3>
                <p className="text-white/55 text-sm leading-relaxed">{s.blurb}</p>
              </motion.div>
            );
          })}
        </motion.div>

        <Reveal delay={0.15} className="mt-14">
          <button
            data-testid="services-cta"
            onClick={go}
            className="group inline-flex items-center gap-2 text-gold text-lg font-display italic hover:gap-3 transition-all"
          >
            Discuss which fits you
            <ArrowUpRight size={20} className="group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
          </button>
        </Reveal>
      </div>
    </section>
  );
};
